import { useEffect, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX, type BridgeMessage } from "@buildlab/preview";
import type { FileMap } from "@buildlab/shared";
import { answerClassDataRequest, answerDataRequest, api, type PlayTest } from "./api";
import { useFit } from "./FitFrame";
import { Alert, Check, Close } from "./icons";
import { emitStep } from "./mission-steps";

// "Check my tests" (level-up #2): the helper turns each plain-words test in PROJECT.md into a short
// play-test, and the app runs them one by one in a fresh copy of the game, on screen, so the student
// watches each check happen. Passing tests can be ticked in PROJECT.md; a failing one becomes a bug
// report for the helper.

type Outcome = { state: "waiting" } | { state: "running" } | { state: "pass" | "fail"; detail: string } | { state: "manual" };

const TEST_TIMEOUT_MS = 15_000; // checks on pages with real data wait for it (up to 12 s)

export function TestLab({
  projectId,
  files,
  onClose,
  onFiles,
  onBugReport,
}: {
  projectId: string;
  files: FileMap;
  onClose: () => void;
  onFiles: (files: FileMap) => void;
  onBugReport: (text: string) => void;
}) {
  const [tests, setTests] = useState<PlayTest[] | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [current, setCurrent] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const snapshot = useRef(files); // the version being checked, fixed for this run
  const fit = useFit(frame, current); // the whole game shows while it is checked (it only changes the look; checks use the page, not the screen)

  useEffect(() => {
    api
      .playTests(projectId)
      .then(({ tests }) => {
        setTests(tests);
        setOutcomes(tests.map((t) => (t.script ? { state: "waiting" } : { state: "manual" })));
        const first = tests.findIndex((t) => t.script);
        setCurrent(first === -1 ? tests.length : first); // nothing to run = done
      })
      .catch((e: Error) => setError(e.message));
  }, [projectId]);

  // Run the current test: a fresh copy of the game loads, then the script plays it.
  useEffect(() => {
    if (!tests || current < 0 || current >= tests.length) return;
    const test = tests[current]!;
    let started = false;
    let finished = false;
    const finish = (o: Outcome) => {
      if (finished) return;
      finished = true;
      setOutcomes((all) => all.map((x, i) => (i === current ? o : x)));
      const next = tests.findIndex((t, i) => i > current && t.script);
      setTimeout(() => setCurrent(next === -1 ? tests.length : next), 600);
    };
    setOutcomes((all) => all.map((x, i) => (i === current ? { state: "running" } : x)));
    const onMessage = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const m = e.data as BridgeMessage;
      if (!m || m.__buildlab !== 1) return;
      if (m.kind === "getData") answerDataRequest(frame.current.contentWindow!, m);
      else if (m.kind === "classData") answerClassDataRequest(frame.current.contentWindow!, m, { projectId });
      else if (m.kind === "ready" && !started) {
        started = true;
        setTimeout(() => frame.current?.contentWindow?.postMessage({ __buildlabTest: 1, id: current, script: test.script }, "*"), 400);
      } else if (m.kind === "test" && m.id === current) {
        finish({ state: m.pass ? "pass" : "fail", detail: m.detail });
      }
    };
    window.addEventListener("message", onMessage);
    const timer = setTimeout(() => finish({ state: "fail", detail: "the check took too long, so it was stopped" }), TEST_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [tests, current]);

  const done = tests !== null && current >= tests.length;
  // Every check ran: a mission step (D45).
  useEffect(() => {
    if (done && tests && tests.length > 0) emitStep({ kind: "checkedTests" });
  }, [done]);
  const passed = tests ? tests.filter((_, i) => outcomes[i]?.state === "pass") : [];
  const checked = outcomes.filter((o) => o.state === "pass" || o.state === "fail");

  return (
    <div className="testlab-backdrop" role="dialog" aria-modal="true" aria-label="Checking your tests">
      <div className="testlab">
        <div className="row between">
          <div>
            <span className="eyebrow">Check my tests</span>
            <h2>{done ? `${passed.length} of ${checked.length} checks passed` : tests ? "The helper is playing your game…" : "Writing a check for each test…"}</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            <Close size={16} />
          </button>
        </div>
        {error && <p className="page-error">{error}</p>}
        {tests?.length === 0 && <p className="hint">There are no tests in PROJECT.md yet. Add some under ## Tests, like “- [ ] Clicking an empty hole doesn't change the score”.</p>}
        {tests && tests.length > 0 && (
          <div className="testlab-body">
            <div ref={fit.stageRef} className={`testlab-stage ${fit.stageClass}`}>
              {!done && current >= 0 && tests[current] && (
                <iframe key={current} ref={frame} style={fit.frameStyle} title="The game, being checked" sandbox={PREVIEW_SANDBOX} srcDoc={buildPreview(snapshot.current, { page: tests[current]!.page }).html} />
              )}
              {done && <p className="hint">All done. Your project wasn't changed by these checks.</p>}
            </div>
            <ol className="testlab-list">
              {tests.map((t, i) => {
                const o = outcomes[i] ?? { state: "waiting" };
                return (
                  <li key={i} className={`tl-${o.state}${i === current && !done ? " now" : ""}`}>
                    <span className="tl-icon" aria-hidden="true">
                      {o.state === "pass" ? <Check size={14} width={3} /> : o.state === "fail" ? <Alert size={14} /> : o.state === "running" ? "…" : o.state === "manual" ? "?" : ""}
                    </span>
                    <div className="tl-text">
                      <b>{t.test}</b>
                      <span className="small">{t.plan}</span>
                      {(o.state === "pass" || o.state === "fail") && <span className="small tl-detail">{o.detail}</span>}
                      {o.state === "manual" && done && (
                        <button
                          type="button"
                          className="linklike small"
                          onClick={async () => {
                            // Tests only a person can check (like "someone finds it in 10 seconds"): the student ticks it after checking.
                            const { files: next } = await api.writeTestResults(projectId, [{ text: t.test, done: true }]);
                            onFiles(next);
                            setOutcomes((all) => all.map((x, j) => (j === i ? { state: "pass", detail: "you checked this one yourself" } : x)));
                          }}
                        >
                          I checked this myself
                        </button>
                      )}
                      {o.state === "fail" && done && (
                        <button
                          type="button"
                          className="linklike small"
                          onClick={() => onBugReport(`I did: checked my test "${t.test}". I expected: it to pass. Instead: ${o.detail}.`)}
                        >
                          Send to helper as a bug report
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
        {done && passed.length > 0 && (
          <div className="row">
            {saved ? (
              <span className="saved-note">
                <Check size={14} stroke="#2E7D5B" width={3} /> Ticked in PROJECT.md
              </span>
            ) : (
              <button
                type="button"
                className="primary small"
                onClick={async () => {
                  const { files: next } = await api.writeTestResults(
                    projectId,
                    tests!.map((t, i) => ({ text: t.test, done: outcomes[i]?.state === "pass" })).filter((_, i) => outcomes[i]?.state !== "manual"),
                  );
                  onFiles(next);
                  setSaved(true);
                }}
              >
                Tick the {passed.length} that passed
              </button>
            )}
            <span className="muted small">Checks that didn't pass get unticked, so your Tests stay honest.</span>
          </div>
        )}
      </div>
    </div>
  );
}
